/**
 * Contacts Page Skeleton
 * Matches the exact layout of ContactsViewVirtual
 *
 * Layout:
 * - Header: Title "All Contacts" + count + Import/Add buttons
 * - Toolbar: Search bar + Sort & Filter button + icon buttons
 * - Table with virtualized contact rows
 *
 * Table columns (from VirtualizedContactList):
 * - Checkbox (w-10)
 * - Name with avatar (w-[200px])
 * - Phone (w-[150px])
 * - Email (flex-1)
 * - Date Added (w-[100px])
 * - Tags (w-[150px])
 * - Actions (w-[100px])
 */

import React from 'react';
import { Skeleton, SkeletonCircle } from './Skeleton';

/**
 * Contact table row skeleton
 * Matches ContactRow component structure
 */
const ContactRowSkeleton: React.FC = () => (
  <div className="relative flex items-stretch" style={{ height: '56px' }}>
    {/* Background layer placeholder */}
    <div className="absolute left-1 right-2 inset-y-0 rounded-lg" />
    {/* Row content */}
    <div className="relative z-10 flex items-stretch w-full">
      {/* Checkbox */}
      <div className="w-10 shrink-0 px-3 py-1 flex items-center justify-center">
        <Skeleton className="w-4 h-4 rounded" />
      </div>
      {/* Name with avatar */}
      <div className="w-[200px] min-w-[120px] px-3 py-1 flex items-center">
        <SkeletonCircle className="w-7 h-7 mr-2 flex-shrink-0" />
        <Skeleton className="h-4 w-28" />
      </div>
      {/* Phone */}
      <div className="w-[150px] min-w-[100px] px-3 py-1 flex items-center justify-center">
        <Skeleton className="h-4 w-24" />
      </div>
      {/* Email */}
      <div className="flex-1 min-w-[150px] px-3 py-1 flex items-center">
        <Skeleton className="h-4 w-40" />
      </div>
      {/* Date Added */}
      <div className="w-[100px] min-w-[80px] px-3 py-1 flex items-center justify-center">
        <Skeleton className="h-4 w-20" />
      </div>
      {/* Tags */}
      <div className="w-[150px] min-w-[90px] px-3 py-1 flex items-center justify-center gap-1">
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      {/* Actions */}
      <div className="w-[100px] min-w-[72px] px-3 py-1 flex items-center justify-center gap-2">
        <Skeleton className="h-7 w-7 rounded-md" />
        <Skeleton className="h-7 w-7 rounded-md" />
      </div>
    </div>
  </div>
);

/**
 * Table header skeleton
 * Matches VirtualizedContactList header structure
 */
const TableHeaderSkeleton: React.FC = () => (
  <div
    className="relative flex select-none items-end border-b border-border"
    style={{ height: '56px' }}
  >
    {/* Checkbox */}
    <div className="w-10 shrink-0 px-3 pb-2 flex items-end justify-center h-full">
      <Skeleton className="w-4 h-4 rounded" />
    </div>
    {/* Name */}
    <div className="w-[200px] min-w-[120px] px-3 pb-2 flex items-end h-full">
      <Skeleton className="h-4 w-12" />
    </div>
    {/* Phone */}
    <div className="w-[150px] min-w-[100px] px-3 pb-2 flex items-end justify-center h-full">
      <Skeleton className="h-4 w-12" />
    </div>
    {/* Email */}
    <div className="flex-1 min-w-[150px] px-3 pb-2 flex items-end h-full">
      <Skeleton className="h-4 w-12" />
    </div>
    {/* Date Added */}
    <div className="w-[100px] min-w-[80px] px-3 pb-2 flex items-end justify-center h-full">
      <Skeleton className="h-4 w-12" />
    </div>
    {/* Tags */}
    <div className="w-[150px] min-w-[90px] px-3 pb-2 flex items-end justify-center h-full">
      <Skeleton className="h-4 w-10" />
    </div>
    {/* Actions */}
    <div className="w-[100px] min-w-[72px] px-3 pb-2 flex items-end justify-center h-full">
      <Skeleton className="h-4 w-14" />
    </div>
  </div>
);

/**
 * Full contacts page skeleton
 * Use this as the loading state for the contacts page
 */
export const ContactsSkeleton: React.FC = () => (
  <div className="h-full flex flex-col p-4">
    {/* Header - matches ContactsHeader */}
    <div className="flex justify-between items-start mb-4">
      <div>
        {/* Title: "All Contacts" */}
        <Skeleton className="h-8 w-36 mb-1" />
        {/* Subtitle: "X contacts" */}
        <Skeleton className="h-4 w-24 mt-1" />
      </div>
      <div className="flex items-center space-x-2">
        {/* Import button */}
        <Skeleton className="h-8 w-20 rounded-md" />
        {/* Add Contact button */}
        <Skeleton className="h-8 w-28 rounded-md" />
      </div>
    </div>

    {/* Toolbar - matches ContactsViewVirtual toolbar */}
    <div className="mb-4 flex items-center gap-3 flex-wrap">
      {/* Search bar */}
      <div className="flex-1 min-w-[240px] max-w-md">
        <Skeleton className="h-9 w-full rounded-md" />
      </div>
      {/* Sort & Filter button */}
      <Skeleton className="h-8 w-28 rounded-md" />
      {/* Message icon button */}
      <Skeleton className="h-8 w-8 rounded-md" />
      {/* Merge icon button */}
      <Skeleton className="h-8 w-8 rounded-md" />
    </div>

    {/* Table - matches VirtualizedContactList */}
    <div className="flex-1 h-full w-full flex flex-col overflow-hidden">
      <TableHeaderSkeleton />
      <div className="flex-1 overflow-hidden">
        {Array.from({ length: 10 }).map((_, i) => (
          <ContactRowSkeleton key={i} />
        ))}
      </div>
    </div>
  </div>
);

export default ContactsSkeleton;
